import React, { useState, useEffect } from 'react';
import './FeedbackPanel.css';

const FeedbackPanel = ({ user, profile, supabaseIntegration, isOpen, onClose }) => {
  const [feedbackType, setFeedbackType] = useState('bug_report');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [recentFeedback, setRecentFeedback] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);

  // Load recent feedback when panel opens
  useEffect(() => {
    if (isOpen && supabaseIntegration?.supabaseHelpers) {
      loadRecentFeedback();
    }
  }, [isOpen, supabaseIntegration]);

  const loadRecentFeedback = async () => {
    setLoadingRecent(true);
    try {
      const { data, error } = await supabaseIntegration.supabaseHelpers.getUserFeedback(user.sub, 5);
      if (error) {
        console.error('Error loading recent feedback:', error);
      } else {
        setRecentFeedback(data || []);
      }
    } catch (error) {
      console.error('Error loading recent feedback:', error);
    } finally {
      setLoadingRecent(false);
    }
  };

  const captureAppContext = () => {
    const context = {
      // Browser info
      userAgent: navigator.userAgent,
      url: window.location.href,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      browserInfo: {
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine
      },
      
      // App state
      appState: {
        timestamp: new Date().toISOString(),
        userRole: profile?.user_role,
        currentPath: window.location.pathname,
        referrer: document.referrer,
        
        // Try to capture some app-specific state
        localStorageKeys: Object.keys(localStorage),
        sessionStorageKeys: Object.keys(sessionStorage)
      }
    };

    return context;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) {
      setSubmitError('Please fill in both title and description');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    try {
      const context = captureAppContext();
      
      const feedbackData = {
        user_id: user.sub,
        type: feedbackType,
        title: title.trim(),
        description: description.trim(),
        priority,
        user_agent: context.userAgent,
        url: context.url,
        screen_resolution: context.screenResolution,
        browser_info: context.browserInfo,
        app_state: context.appState
      };

      const { data, error } = await supabaseIntegration.supabaseHelpers.submitFeedback(feedbackData);
      
      if (error) {
        throw new Error(error.message || 'Failed to submit feedback');
      }

      // Success
      setSubmitSuccess(true);
      setTitle('');
      setDescription('');
      setPriority('medium');
      
      // Reload recent feedback
      loadRecentFeedback();
      
      // Log the submission
      if (supabaseIntegration.logUserEvent) {
        supabaseIntegration.logUserEvent('feedback_submitted', {
          feedback_id: data?.id,
          type: feedbackType,
          priority,
          title: title.trim()
        });
      }

      // Auto-close success message after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitError(error.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'bug_report': return '🐛';
      case 'feature_request': return '💡';
      case 'general_feedback': return '💬';
      default: return '📝';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return '#ff4444';
      case 'high': return '#ff8800';
      case 'medium': return '#3b82f6';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return '#3b82f6';
      case 'in_progress': return '#f59e0b';
      case 'resolved': return '#10b981';
      case 'closed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-panel-overlay">
      <div className="feedback-panel">
        <div className="feedback-panel-header">
          <div className="feedback-panel-title">
            <span className="feedback-icon">📝</span>
            <h2>Submit Feedback</h2>
          </div>
          <button className="feedback-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="feedback-panel-content">
          {/* Submit Form */}
          <div className="feedback-form-section">
            <h3>Report a Bug or Request a Feature</h3>
            
            {submitSuccess && (
              <div className="feedback-success">
                ✅ Feedback submitted successfully! Thank you for helping improve PopMates.
              </div>
            )}
            
            {submitError && (
              <div className="feedback-error">
                ❌ {submitError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="feedback-form">
              <div className="feedback-form-row">
                <div className="feedback-form-group">
                  <label>Type</label>
                  <select 
                    value={feedbackType} 
                    onChange={(e) => setFeedbackType(e.target.value)}
                    className="feedback-select"
                  >
                    <option value="bug_report">🐛 Bug Report</option>
                    <option value="feature_request">💡 Feature Request</option>
                    <option value="general_feedback">💬 General Feedback</option>
                  </select>
                </div>

                <div className="feedback-form-group">
                  <label>Priority</label>
                  <select 
                    value={priority} 
                    onChange={(e) => setPriority(e.target.value)}
                    className="feedback-select"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>

              <div className="feedback-form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief description of the issue or request"
                  className="feedback-input"
                  maxLength={200}
                  required
                />
                <div className="feedback-char-count">{title.length}/200</div>
              </div>

              <div className="feedback-form-group">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Detailed description. For bugs, include steps to reproduce. For features, explain the desired functionality."
                  className="feedback-textarea"
                  rows={6}
                  maxLength={2000}
                  required
                />
                <div className="feedback-char-count">{description.length}/2000</div>
              </div>

              <div className="feedback-context-note">
                <span className="feedback-context-icon">ℹ️</span>
                <span>Technical details (browser, screen size, app state) will be automatically captured to help with debugging.</span>
              </div>

              <button 
                type="submit" 
                className="feedback-submit-btn"
                disabled={isSubmitting || !title.trim() || !description.trim()}
              >
                {isSubmitting ? (
                  <>
                    <span className="feedback-spinner">⏳</span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <span>📤</span>
                    Submit Feedback
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Recent Feedback */}
          <div className="feedback-recent-section">
            <h3>Your Recent Feedback</h3>
            
            {loadingRecent ? (
              <div className="feedback-loading">
                <span className="feedback-spinner">⏳</span>
                Loading recent feedback...
              </div>
            ) : recentFeedback.length === 0 ? (
              <div className="feedback-empty">
                <span className="feedback-empty-icon">📝</span>
                <p>No feedback submitted yet</p>
              </div>
            ) : (
              <div className="feedback-recent-list">
                {recentFeedback.map((item) => (
                  <div key={item.id} className="feedback-recent-item">
                    <div className="feedback-recent-header">
                      <div className="feedback-recent-type">
                        <span className="feedback-type-icon">{getTypeIcon(item.type)}</span>
                        <span className="feedback-type-text">
                          {item.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                      <div className="feedback-recent-meta">
                        <span 
                          className="feedback-priority-badge"
                          style={{ backgroundColor: getPriorityColor(item.priority) }}
                        >
                          {item.priority}
                        </span>
                        <span 
                          className="feedback-status-badge"
                          style={{ backgroundColor: getStatusColor(item.status) }}
                        >
                          {item.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <div className="feedback-recent-title">{item.title}</div>
                    <div className="feedback-recent-date">
                      Submitted {formatDate(item.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPanel; 